import assertNever from "assert-never";
import type { Control } from "react-hook-form";

import type { ComposerParamByType, ComposerParamType } from "../lib/params.js";

import ComposerArrayParam from "./ComposerArrayParam.js";
import ComposerBooleanParam from "./ComposerBooleanParam.js";
import ComposerIntegerParam from "./ComposerIntegerParam.js";
import ComposerNullParam from "./ComposerNullParam.js";
import ComposerObjectParam from "./ComposerObjectParam.js";
import ComposerOneOfParam from "./ComposerOneOfParam.js";
import ComposerStringParam from "./ComposerStringParam.js";
import ComposerTupleParam from "./ComposerTupleParam.js";

export type ComposerParamComponentProps<Type extends ComposerParamType> = {
  control: Control;
  name: string;
  param: ComposerParamByType<Type>;
};

interface ComposerParamInputProps
  extends ComposerParamComponentProps<ComposerParamType> {
  nestedIndex?: number;
}

const ComposerParamInput = ({
  control,
  name,
  param,
  nestedIndex,
}: ComposerParamInputProps) => {
  if (!param) {
    return null;
  }

  switch (param.type) {
    case "string":
      return (
        <ComposerStringParam control={control} name={name} param={param} />
      );
    case "integer":
    case "number":
      return (
        <ComposerIntegerParam control={control} name={name} param={param} />
      );
    case "boolean":
      return (
        <ComposerBooleanParam control={control} name={name} param={param} />
      );
    case "null":
      return <ComposerNullParam control={control} name={name} param={param} />;
    case "array":
      return (
        <ComposerArrayParam
          control={control}
          name={name}
          param={param}
          nestedIndex={nestedIndex}
        />
      );
    case "tuple":
      return (
        <ComposerTupleParam
          control={control}
          name={name}
          param={param}
          nestedIndex={nestedIndex}
        />
      );
    case "object":
      return (
        <ComposerObjectParam
          control={control}
          name={name}
          param={param}
          nestedIndex={nestedIndex}
        />
      );
    case "oneOf":
    case "anyOf":
      return (
        <ComposerOneOfParam
          control={control}
          name={name}
          param={param}
          nestedIndex={nestedIndex}
        />
      );
    default:
      assertNever(param);
  }
};

export default ComposerParamInput;
